import { NavLink } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import useAdminAuth from "../hooks/useAdminAuth";

type DrawerKey =
  | "productManagement"
  | "ordersPayments"
  | "websiteControls"
  | "userManagement"
  | "settings"
  | null;

interface SidebarItem {
  label: string;
  path: string;
  modules: string[];
  end?: boolean;
}

interface AdminSidebarMenuProps {
  onNavigate?: () => void;
}

export default function AdminSidebarMenu({
  onNavigate,
}: AdminSidebarMenuProps) {
  const { admin, loading, rightsLoading, canViewAny } = useAdminAuth();
  const [openDrawer, setOpenDrawer] = useState<DrawerKey>(null);

  const isSuperAdmin = Number(admin?.group_id) === 1;

  const canAccess = (modules: string[]) => {
    if (isSuperAdmin) return true;
    return canViewAny(modules);
  };

  const toggleDrawer = (drawer: DrawerKey) => {
    setOpenDrawer((prev) => (prev === drawer ? null : drawer));
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `sidebar-link ${isActive ? "active" : ""}`;

  const dashboardItem: SidebarItem = {
    label: "Dashboard",
    path: "/admin",
    modules: ["dashboard"],
    end: true,
  };

  const productManagementItems = useMemo(
    () => [
      {
        label: "Product Categories",
        path: "/admin/categories",
        modules: ["categories", "category", "product_categories"],
      },
      {
        label: "Subcategories",
        path: "/admin/subcategories",
        modules: ["subcategories", "subcategory", "product_subcategories"],
      },
      {
        label: "Product Types",
        path: "/admin/types",
        modules: ["types", "product_types", "product_type"],
      },
      {
        label: "Brands",
        path: "/admin/brands",
        modules: ["brands", "brands"],
      },
      {
        label: "Products",
        path: "/admin/products",
        modules: ["products", "product"],
      },
    ],
    [],
  );

  const ordersPaymentsItems = useMemo(
    () => [
      {
        label: "Orders",
        path: "/admin/orders",
        modules: ["orders", "order"],
      },
      {
        label: "Payments",
        path: "/admin/payments",
        modules: ["payments", "payment"],
      },
    ],
    [],
  );

  const supportTicketItem: SidebarItem = {
    label: "Support Tickets",
    path: "/admin/support-tickets",
    modules: ["support_tickets", "supportTickets", "support-tickets"],
  };

  const websiteControlItems = useMemo(
    () => [
      {
        label: "Subscribers",
        path: "/admin/subscribers",
        modules: ["subscribers", "subscriber"],
      },
      {
        label: "Demo Bookings",
        path: "/admin/demo-bookings",
        modules: ["demo_bookings", "demoBookings", "book_demo"],
      },
      {
        label: "Customer Reviews",
        path: "/admin/reviews",
        modules: ["reviews", "customer_reviews", "customerReviews"],
      },
      {
        label: "About Us",
        path: "/admin/about",
        modules: ["about", "about_us", "aboutUs"],
      },
      {
        label: "Career",
        path: "/admin/admin-career",
        modules: ["career", "careers", "career_jobs"],
      },
      {
        label: "Job Applications",
        path: "/admin/career-applications",
        modules: ["career_applications", "job_applications", "applications"],
      },
      {
        label: "Latest News",
        path: "/admin/news",
        modules: ["news", "latest_news", "latestNews"],
      },
      {
        label: "Services",
        path: "/admin/services",
        modules: ["services", "service"],
      },
      {
        label: "Software",
        path: "/admin/software",
        modules: ["software", "softwares"],
      },
      {
        label: "Testimonials",
        path: "/admin/testimonials",
        modules: ["testimonials", "testimonial"],
      },
      {
        label: "Team",
        path: "/admin/team",
        modules: ["team", "team_members", "teamMembers"],
      },
      {
        label: "Site Footer",
        path: "/admin/footer",
        modules: ["footer", "site_footer", "siteFooter"],
      },
      {
        label: "Site Search",
        path: "/admin/site-search",
        modules: ["site_search", "siteSearch", "search"],
      },
    ],
    [],
  );

  const userManagementItems = useMemo(
    () => [
      {
        label: "Admin Users",
        path: "/admin/users",
        modules: ["admin_users", "adminUsers", "users"],
      },
      {
        label: "User Groups",
        path: "/admin/groups",
        modules: ["user_groups", "userGroups", "groups"],
      },
      {
        label: "User Rights",
        path: "/admin/user-rights",
        modules: ["user_rights", "userRights", "rights"],
      },
      {
        label: "Regions",
        path: "/admin/regions",
        modules: ["regions", "region"],
      },
    ],
    [],
  );

  const settingsItems = useMemo(
    () => [
      {
        label: "Promotions",
        path: "/admin/promotions",
        modules: ["promotions", "promotion"],
      },
      {
        label: "Discount Vouchers",
        path: "/admin/discount-vouchers",
        modules: ["discount_vouchers", "discountVouchers", "vouchers"],
      },
      {
        label: "Algorithm Setup",
        path: "/admin/algorithm",
        modules: ["algorithm", "algorithm_setup", "algorithmSetup"],
      },
      {
        label: "Promotion Banners",
        path: "/admin/banners",
        modules: ["banners", "promotion_banners", "promotionBanners"],
      },
      {
        label: "Email Templates",
        path: "/admin/emails",
        modules: ["emails", "email_templates", "emailTemplates"],
      },
      {
        label: "Email Campaigns",
        path: "/admin/send-campaign",
        modules: ["send_campaign", "sendCampaign", "email_campaigns"],
      },
      {
        label: "Public Emails",
        path: "/admin/public-emails",
        modules: ["public_emails", "publicEmails"],
      },
      {
        label: "Email Variables",
        path: "/admin/email-variables",
        modules: ["email_variables", "emailVariables"],
      },
      {
        label: "SMTP Settings",
        path: "/admin/smtp-settings",
        modules: ["smtp_settings", "smtpSettings"],
      },
      {
        label: "Payment Gateways",
        path: "/admin/payment-gateways",
        modules: ["payment_gateways", "paymentGateways"],
      },
      {
        label: "Policies",
        path: "/admin/policies",
        modules: ["policies", "policy"],
      },
      {
        label: "Audit Trail",
        path: "/admin/auditTrail",
        modules: ["audit_trail", "auditTrail", "audit"],
      },
    ],
    [],
  );

  const visibleProductManagementItems = productManagementItems.filter((item) =>
    canAccess(item.modules),
  );

  const visibleOrdersPaymentsItems = ordersPaymentsItems.filter((item) =>
    canAccess(item.modules),
  );

  const visibleWebsiteControlItems = websiteControlItems.filter((item) =>
    canAccess(item.modules),
  );

  const visibleUserManagementItems = userManagementItems.filter((item) =>
    canAccess(item.modules),
  );

  const visibleSettingsItems = settingsItems.filter((item) =>
    canAccess(item.modules),
  );

  const showDashboard = canAccess(dashboardItem.modules);
  const showSupportTickets = canAccess(supportTicketItem.modules);

  const showProductManagement = visibleProductManagementItems.length > 0;
  const showOrdersPayments = visibleOrdersPaymentsItems.length > 0;
  const showWebsiteControls = visibleWebsiteControlItems.length > 0;
  const showUserManagement = visibleUserManagementItems.length > 0;
  const showSettings = visibleSettingsItems.length > 0;

  useEffect(() => {
    if (
      (openDrawer === "productManagement" && !showProductManagement) ||
      (openDrawer === "ordersPayments" && !showOrdersPayments) ||
      (openDrawer === "websiteControls" && !showWebsiteControls) ||
      (openDrawer === "userManagement" && !showUserManagement) ||
      (openDrawer === "settings" && !showSettings)
    ) {
      setOpenDrawer(null);
    }
  }, [
    openDrawer,
    showProductManagement,
    showOrdersPayments,
    showWebsiteControls,
    showUserManagement,
    showSettings,
  ]);

  const renderLink = (item: SidebarItem) => (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.end}
      className={navClass}
      onClick={onNavigate}
    >
      {item.label}
    </NavLink>
  );

  const renderDrawer = (
    drawerKey: Exclude<DrawerKey, null>,
    title: string,
    items: SidebarItem[],
  ) => {
    if (items.length === 0) return null;

    return (
      <div className="sidebar-drawer">
        <button
          type="button"
          className="sidebar-link drawer-toggle"
          onClick={() => toggleDrawer(drawerKey)}
        >
          <span>{title}</span>
          <span>{openDrawer === drawerKey ? "−" : "+"}</span>
        </button>

        {openDrawer === drawerKey && (
          <div className="drawer-items">
            {items.map((item) => renderLink(item))}
          </div>
        )}
      </div>
    );
  };

  if (loading || rightsLoading) {
    return (
      <div className="sidebar-link" style={{ opacity: 0.7 }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      {showDashboard && renderLink(dashboardItem)}

      {renderDrawer(
        "productManagement",
        "Product Management",
        visibleProductManagementItems,
      )}

      {renderDrawer(
        "ordersPayments",
        "Orders & Payments",
        visibleOrdersPaymentsItems,
      )}

      {showSupportTickets && renderLink(supportTicketItem)}

      {renderDrawer(
        "websiteControls",
        "Website Controls",
        visibleWebsiteControlItems,
      )}

      {renderDrawer(
        "userManagement",
        "User Management",
        visibleUserManagementItems,
      )}

      {renderDrawer("settings", "Settings", visibleSettingsItems)}
    </>
  );
}
